import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

export interface DataTableColumn<TRow> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: TRow) => ReactNode;
  /** Column width for the grid track (e.g. '120px', 'minmax(0, 2fr)'). Defaults to an equal share. */
  readonly width?: string;
  /** Right-align header and cell (use for numeric columns). */
  readonly align?: 'start' | 'end';
}

interface DataTableProps<TRow> {
  readonly columns: readonly DataTableColumn<TRow>[];
  readonly rows: readonly TRow[];
  readonly rowKey: (row: TRow) => string;
  /** Makes rows clickable/keyboard-selectable. */
  readonly onRowSelect?: (row: TRow) => void;
  /** Rechtsklick auf eine Zeile -- für das FORQ-eigene Kontextmenü. */
  readonly onRowContext?: (event: MouseEvent<HTMLDivElement>, row: TRow) => void;
  readonly isRowSelected?: (row: TRow) => boolean;
  /** Subtly tints a row to signal state (e.g. negative stock). */
  readonly rowTone?: (row: TRow) => 'danger' | 'warning' | 'info' | undefined;
  readonly loading?: boolean;
  readonly loadingLabel?: string;
  readonly emptyLabel?: string;
  readonly ariaLabel?: string;
  /** Drop the outer border/background so the table sits flush inside an existing panel. */
  readonly flush?: boolean;
}

/**
 * Dense, keyboard-selectable list used across every module. Grid-based (not a <table>)
 * so the shared styling lives here, not in a global override layer.
 */
export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  onRowSelect,
  onRowContext,
  isRowSelected,
  rowTone,
  loading = false,
  loadingLabel = 'Daten werden geladen …',
  emptyLabel = 'Keine Einträge vorhanden.',
  ariaLabel,
  flush = false,
}: DataTableProps<TRow>) {
  const interactive = Boolean(onRowSelect);
  const template = columns.map((column) => column.width ?? 'minmax(0, 1fr)').join(' ');

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>, row: TRow): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowSelect?.(row);
    }
  }

  return (
    <div className={`forq-data-table${flush ? ' forq-data-table--flush' : ''}`} role="table" aria-label={ariaLabel}>
      <div className="forq-data-table__head" role="row" style={{ gridTemplateColumns: template }}>
        {columns.map((column) => (
          <span key={column.key} role="columnheader" className={column.align === 'end' ? 'is-end' : undefined}>
            {column.header}
          </span>
        ))}
      </div>
      <div className="forq-data-table__body">
        {loading ? (
          <div className="forq-data-table__state">{loadingLabel}</div>
        ) : rows.length === 0 ? (
          <div className="forq-data-table__state">{emptyLabel}</div>
        ) : (
          rows.map((row) => {
            const selected = isRowSelected?.(row) ?? false;
            const tone = rowTone?.(row);
            return (
              <div
                key={rowKey(row)}
                role="row"
                className={`forq-data-table__row${tone ? ` forq-data-table__row--${tone}` : ''}${selected ? ' is-selected' : ''}`}
                style={{ gridTemplateColumns: template }}
                tabIndex={interactive ? 0 : undefined}
                aria-selected={interactive ? selected : undefined}
                onClick={interactive ? () => onRowSelect?.(row) : undefined}
                onContextMenu={onRowContext ? (event) => onRowContext(event, row) : undefined}
                onKeyDown={interactive ? (event) => handleKeyDown(event, row) : undefined}
              >
                {columns.map((column) => (
                  <span key={column.key} role="cell" className={column.align === 'end' ? 'is-end' : undefined}>
                    {column.render(row)}
                  </span>
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
